import axios, { AxiosResponse } from "axios";
import { CurrentUser, CurrentUserFromJSON } from "../bitmaker";

export class BitmakerApp {
  private configuration: any;

  constructor(configuration?: any) {
    this.configuration = configuration;
  }

  public getUserDetails(
    callback: (user: CurrentUser | null) => void,
    tokenExpirationTime: number
  ) {
    this.get("/users/me", { tokenExpirationTime }, (response) => {
      if (response && response.data) {
        const userObj = CurrentUserFromJSON(response.data);
        window.localStorage.setItem("bmCurrentUser", JSON.stringify(userObj));
        callback(userObj);
      } else {
        callback(null);
      }
    });
  }

  public refreshAccessToken(callback: (expiresIn: number | null) => void) {
    this.post("/google-auth/refresh", {}, (response) => {
      if (response && response.data && response.data.expires_in) {
        const expiresIn: number = response.data.expires_in;
        callback(expiresIn);
      } else {
        callback(null);
      }
    });
  }

  public removeUser() {
    this.post("/google-auth/logout", {}, (response) => {
      if (!response || !response.data) {
        console.error("Error during logout");
      }
    });

    window.localStorage.removeItem("bmCurrentUser");
    window.localStorage.removeItem("tokenExpiration");
  }

  async get(
    path: string,
    requestParams: any,
    callback: (response: AxiosResponse<any>) => any,
    axiosConfig: any = {}
  ) {
    axios
      .get(this.configuration.basePath + path, {
        withCredentials: true,
        headers: this.configuration.headers,
        params: requestParams == null ? {} : requestParams,
        ...axiosConfig,
      })
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        callback(error);
      });
  }

  public getBlob(
    url: string,
    data: any,
    callback: (response: any) => any
  ): Promise<void> {
    return this.get(url, data, callback, {
      responseType: "blob",
      headers: {
        Accept: "application/pdf",
      },
    });
  }

  async post(
    path: string,
    requestBody: any,
    callback: (response: AxiosResponse<any>) => any
  ) {
    axios
      .post(this.configuration.basePath + path, requestBody, {
        withCredentials: true,
        headers: this.configuration.headers,
      })
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        callback(error);
      });
  }

  async put(
    path: string,
    requestBody: any,
    callback: (response: AxiosResponse<any>) => any
  ) {
    axios
      .put(this.configuration.basePath + path, requestBody, {
        withCredentials: true,
        headers: this.configuration.headers,
      })
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        callback(error);
      });
  }

  async delete(
    path: string,
    requestBody: any = {},
    callback: (response: AxiosResponse<any>) => any
  ) {
    axios
      .delete(this.configuration.basePath + path, {
        data: requestBody,
        withCredentials: true,
        headers: this.configuration.headers,
      })
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        callback(error);
      });
  }

  async request(
    method: string,
    path: string,
    requestBody: any,
    callback: (response: AxiosResponse<any>) => any
  ) {
    axios({
      method: method,
      url: this.configuration.basePath + path,
      data: requestBody,
      withCredentials: true,
      headers: this.configuration.headers,
    })
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        callback(error);
      });
  }
}

const configuration = {
  basePath: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
};

export const BitmakerAPIClient = new BitmakerApp(configuration);

export type HTTPMethod = "get" | "post" | "put" | "delete";

export const methodMap: Record<
  HTTPMethod,
  (url: string, data: any, callback: (response: any) => any) => Promise<void>
> = {
  get: BitmakerAPIClient.get.bind(BitmakerAPIClient),
  post: BitmakerAPIClient.post.bind(BitmakerAPIClient),
  put: BitmakerAPIClient.put.bind(BitmakerAPIClient),
  delete: BitmakerAPIClient.delete.bind(BitmakerAPIClient),
};
